import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Badge, Spinner, Button, Row, Col } from '@/components/ui';
import { workIdService } from '../services/workIdService';
import type { WorkIdDetail } from '../services/workIdService';

interface RelatedWorkIdsTabProps {
  workIdId: number | null | undefined;
  workIdentificationId?: string | null;
  /** When provided with onUnlink, enables the Unlink button to remove the work ID from this task. */
  taskId?: number | null;
  /** Called after successfully unlinking (e.g. to refresh task). */
  onUnlink?: () => void;
}

function formatClosureDate(workId: WorkIdDetail | null): string | null {
  if (!workId || (workId.status || '').toUpperCase() !== 'CLOSED') return null;
  const tasks = workId.tasks || [];
  const withDate = tasks
    .map((t) => (t as { completionDate?: string | null }).completionDate)
    .filter(Boolean) as string[];
  if (withDate.length === 0) return null;
  const sorted = [...withDate].sort();
  const latest = sorted[sorted.length - 1];
  try {
    const d = new Date(latest);
    if (isNaN(d.getTime())) return null;
    return d.toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return null;
  }
}

const RelatedWorkIdsTab: React.FC<RelatedWorkIdsTabProps> = ({
  workIdId,
  workIdentificationId: initialWorkIdentificationId,
  taskId,
  onUnlink,
}) => {
  const [workId, setWorkId] = useState<WorkIdDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [unlinking, setUnlinking] = useState(false);

  useEffect(() => {
    if (!workIdId) return;
    setLoading(true);
    workIdService
      .getById(workIdId)
      .then(setWorkId)
      .catch(() => setWorkId(null))
      .finally(() => setLoading(false));
  }, [workIdId]);

  const handleUnlink = async () => {
    if (!workIdId || taskId == null || !onUnlink) return;
    setUnlinking(true);
    try {
      await workIdService.unassign(workIdId, taskId);
      onUnlink();
    } catch (err) {
      console.error('Unlink failed:', err);
      alert(err instanceof Error ? err.message : 'Failed to unlink work ID');
    } finally {
      setUnlinking(false);
    }
  };

  if (!workIdId) return null;

  const displayId = workId?.workIdentificationId ?? initialWorkIdentificationId ?? `#${workIdId}`;
  const status = workId?.status ?? '';
  const statusUpper = status ? status.toUpperCase() : '';
  const closureDateStr = formatClosureDate(workId);
  const canUnlink = taskId != null && typeof onUnlink === 'function';
  const isWorkIdClosed = statusUpper === 'CLOSED';
  const unlinkDisabled = unlinking || isWorkIdClosed;

  const descriptionParts: string[] = [];
  if (workId?.foremanArea?.name) {
    descriptionParts.push(workId.foremanArea.name);
  }
  const descriptionLine = descriptionParts.length ? descriptionParts.join(' · ') : null;

  const reportedByName =
    workId?.reportedBy?.name && workId?.reportedBy?.lastName
      ? `${workId.reportedBy.name} ${workId.reportedBy.lastName}`
      : workId?.reportedBy?.name || workId?.reportedBy?.lastName || '—';
  const foremanAreaName = workId?.foremanArea?.name ?? '—';

  return (
    <div className="tw-py-3">
      {/* Section header: LINKED WORK IDS + count badge */}
      <div className="tw-flex tw-items-center tw-gap-2 tw-mb-3">
        <span className="tw-uppercase tw-text-content-muted tw-text-sm tw-font-semibold">Linked Work IDs</span>
        <Badge pill bg="secondary">1</Badge>
      </div>

      {loading ? (
        <div className="tw-text-content-muted tw-text-sm">
          <Spinner as="span" animation="border" size="sm" className="tw-mr-2" aria-hidden="true" />
          Loading Work ID details…
        </div>
      ) : workId ? (
        <>
          {/* Work ID card */}
          <div className="tw-border tw-border-line tw-rounded tw-p-3 tw-bg-surface-alt tw-mb-3">
            <div className="tw-flex tw-items-start tw-gap-3 tw-flex-wrap">
              <div className="tw-flex tw-items-center tw-justify-center tw-rounded tw-bg-surface tw-border tw-border-line tw-shrink-0" style={{ width: 40, height: 40 }}>
                <i className="bi bi-file-earmark-text tw-text-content-secondary" aria-hidden />
              </div>
              <div className="tw-grow min-w-0">
                <div className="tw-flex tw-items-center tw-gap-2 tw-flex-wrap">
                  <Link
                    to={`/work-ids?search=${encodeURIComponent(displayId)}`}
                    className="tw-font-bold tw-text-primary tw-no-underline"
                  >
                    {displayId}
                  </Link>
                  {status && (
                    <Badge
                      pill
                      bg={
                        statusUpper === 'ASSIGNED'
                          ? 'info'
                          : statusUpper === 'CLOSED'
                            ? 'secondary'
                            : 'warning'
                      }
                    >
                      {statusUpper}
                    </Badge>
                  )}
                </div>
                {descriptionLine && (
                  <div className="tw-text-content-muted tw-text-sm tw-mt-1">{descriptionLine}</div>
                )}
              </div>
              <div className="tw-flex tw-items-center tw-gap-2 tw-shrink-0">
                {closureDateStr && (
                  <span className="tw-text-content-muted tw-text-sm">Closed {closureDateStr}</span>
                )}
                {canUnlink && (
                  <Button
                    type="button"
                    variant="danger"
                    size="sm"
                    onClick={handleUnlink}
                    disabled={unlinkDisabled}
                    title={isWorkIdClosed ? 'Cannot unlink a closed work ID' : undefined}
                  >
                    {unlinking ? 'Unlinking…' : 'Unlink'}
                  </Button>
                )}
              </div>
            </div>
            {/* Detail grid: REPORTED BY, FOREMAN AREA */}
            <Row className="tw-mt-2 tw-pt-2 tw-border-t tw-border-line">
              <Col md={4}>
                <div className="tw-uppercase tw-text-content-muted tw-text-sm">Reported By</div>
                <div className="tw-font-semibold">{reportedByName}</div>
              </Col>
              <Col md={4}>
                <div className="tw-uppercase tw-text-content-muted tw-text-sm">Foreman Area</div>
                <div className="tw-font-semibold">{foremanAreaName}</div>
              </Col>
            </Row>
          </div>
        </>
      ) : (
        <div className="tw-text-content-muted tw-text-sm">
          Unable to load work ID details.
        </div>
      )}
    </div>
  );
};

export default RelatedWorkIdsTab;
